import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Workspace } from "../lib/api";
import { LoadingButton } from "./Spinner";
import { useConfirm } from "./ConfirmDialog";
import { copyToClipboard } from "./Toast";
import RelativeTime from "./RelativeTime";

interface Props {
  workspace: Workspace;
  open: boolean;
  onClose: () => void;
}

// Manage-agents overlay. List view only; the create flow lives in its
// own focused NewAgentModal so users get clear, numbered
// install/login/run instructions instead of a wall of cards.
export default function AgentsModal({ workspace, open, onClose }: Props) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [newOpen, setNewOpen] = useState(false);

  const list = useQuery({
    queryKey: ["agents", workspace.id],
    queryFn: () => api.agents.list(workspace.id),
    refetchInterval: open ? 5_000 : false,
    enabled: open,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.agents.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agents", workspace.id] }),
  });

  // Escape to close, like other modals.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const agents = list.data?.agents ?? [];

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4 bg-[color:var(--backdrop)]"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-3xl h-[min(820px,90vh)] overflow-hidden flex flex-col shadow-[var(--shadow-strong)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold">Agents</h2>
            <span className="text-xs muted-strong">— {workspace.name}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="h-8 w-8 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
          >×</button>
        </div>

        <div className="flex-1 min-h-0 overflow-auto scroll-pane p-5 space-y-5 bg-bg">
          <p className="text-sm muted">
            An agent runs on your machine (or a server) and dispatches outbound API requests for you. Anything the agent can reach — private networks, VPN, custom certs — your requests can hit. Without an agent, requests run from the cloud worker.
          </p>

          <div className="card p-5 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <h3 className="font-semibold">Your agents</h3>
                <span className="text-xs muted-strong truncate">{agents.length} total · {agents.filter((a) => a.online).length} online</span>
              </div>
              <button type="button" className="btn-primary shrink-0" onClick={() => setNewOpen(true)}>+ New agent</button>
            </div>
            {agents.length === 0 && (
              <div className="empty-card text-center space-y-2 py-6">
                <p>No agents yet.</p>
                <button type="button" className="btn-primary" onClick={() => setNewOpen(true)}>+ New agent</button>
              </div>
            )}
            <ul className="space-y-2">
              {agents.map((a) => (
                <li key={a.id} className="flex items-center gap-3 rounded-md border border-border bg-surface-2 p-3">
                  <span
                    className={"h-2.5 w-2.5 rounded-full shrink-0 " + (a.online ? "bg-good" : "bg-text-faint")}
                    aria-label={a.online ? "online" : "offline"}
                    title={a.online ? "online" : "offline"}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{a.name}</div>
                    <div className="text-xs muted-strong truncate flex items-center gap-1">
                      {a.online && <span>online</span>}
                      {!a.online && a.last_seen_at && (
                        <>
                          <span>last seen</span>
                          <RelativeTime iso={a.last_seen_at} />
                        </>
                      )}
                      {!a.online && !a.last_seen_at && <span>never connected</span>}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      if (await confirm({
                        title: `Delete "${a.name}"?`,
                        description: "The agent's token stops working immediately and the entry is removed from this list.",
                        danger: true,
                      })) remove.mutate(a.id);
                    }}
                    title="Delete agent"
                    aria-label="Delete agent"
                    className="h-8 w-8 grid place-items-center rounded text-text-faint hover:text-danger hover:bg-surface-3"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <NewAgentModal
        workspace={workspace}
        open={newOpen}
        onClose={() => setNewOpen(false)}
      />
    </div>
  );
}

// Step-by-step create flow. Three states:
//   1) name input (before creation)
//   2) instructions screen with numbered steps + the just-minted token
//   3) closed by user → back to step 1 on next open
function NewAgentModal({ workspace, open, onClose }: { workspace: Workspace; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState("My machine");
  const [created, setCreated] = useState<{ id: string; name: string; token: string; wsUrl: string } | null>(null);

  const create = useMutation({
    mutationFn: () => api.agents.create(workspace.id, name.trim() || "Agent"),
    onSuccess: (a) => {
      setCreated(a);
      qc.invalidateQueries({ queryKey: ["agents", workspace.id] });
    },
  });

  // Reset whenever the modal closes so the next open starts fresh.
  useEffect(() => {
    if (!open) {
      // Defer so the close animation can see the success state if any.
      const t = setTimeout(() => { setCreated(null); setName("My machine"); }, 200);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] grid place-items-center p-4 bg-[color:var(--backdrop)]"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-xl h-auto max-h-[min(820px,90vh)] overflow-hidden flex flex-col shadow-[var(--shadow-strong)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <h2 className="text-base font-semibold">{created ? `Connect "${created.name}"` : "New agent"}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="h-8 w-8 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
          >×</button>
        </div>

        <div className="flex-1 min-h-0 overflow-auto scroll-pane p-5 space-y-4">
          {!created && (
            <form
              className="space-y-4"
              onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
            >
              <p className="text-sm muted">
                Give your agent a name so you can tell machines apart in the executor menu.
              </p>
              <div className="space-y-1.5">
                <label className="label">Name</label>
                <input
                  className="input w-full"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="My machine"
                  autoFocus
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <button type="button" className="btn" onClick={onClose}>Cancel</button>
                <LoadingButton type="submit" className="btn-primary" pending={create.isPending} pendingLabel="Creating…">
                  Create &amp; show instructions
                </LoadingButton>
              </div>
            </form>
          )}

          {created && (
            <ConnectInstructions
              agent={created}
              onDone={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// Three numbered steps with copy buttons. Layout designed to be
// followed top-to-bottom — paste step 1 into a terminal, paste step
// 2, paste step 3.
function ConnectInstructions({
  agent, onDone,
}: {
  agent: { id: string; name: string; token: string; wsUrl: string };
  onDone: () => void;
}) {
  const installCmd = `curl -fsSL https://apistash.dev/install-agent.sh | sh`;
  const loginCmd = `apistash-agent login '${agent.wsUrl}'`;
  const runCmd = `apistash-agent run`;
  return (
    <div className="space-y-4 min-w-0">
      <p className="text-xs muted">
        The token inside the login command is only shown <strong className="text-text">once</strong>. If you lose it, delete this agent and create a new one.
      </p>

      <Step
        number={1}
        title="Install the agent"
        description={<>On the machine that should run your requests (needs <a className="text-accent hover:underline" href="https://nodejs.org" target="_blank" rel="noreferrer">Node.js 18+</a>):</>}
        command={installCmd}
        copyLabel="install command"
      />
      <Step
        number={2}
        title="Log in"
        description="Connect it to this workspace:"
        command={loginCmd}
        copyLabel="login command"
      />
      <Step
        number={3}
        title="Start the agent"
        description="Keep this running. Reconnects automatically if dropped."
        command={runCmd}
        copyLabel="run command"
      />
      <Step
        number={4}
        title="Pick it in the executor menu"
        description={
          <>
            Back in the topbar, open the executor dropdown — your agent shows up as <strong>{agent.name}</strong> with a green online dot. Pick it and every request you fire will dispatch through that machine.
          </>
        }
      />

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
        <button type="button" className="btn-primary" onClick={onDone}>Done</button>
      </div>
    </div>
  );
}

function Step({
  number, title, description, command, copyLabel,
}: {
  number: number;
  title: string;
  description: React.ReactNode;
  command?: string;
  copyLabel?: string;
}) {
  return (
    <div className="flex gap-3 min-w-0">
      <div className="shrink-0 h-7 w-7 rounded-full bg-accent-soft text-accent-bright grid place-items-center font-semibold text-sm border border-[color-mix(in_srgb,var(--accent)_30%,transparent)]">
        {number}
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        <div>
          <div className="font-semibold text-sm">{title}</div>
          <div className="text-xs muted mt-0.5">{description}</div>
        </div>
        {command && (
          <div className="relative min-w-0">
            <pre className="code-block !pr-16">{command}</pre>
            <button
              type="button"
              onClick={() => copyToClipboard(command, copyLabel ? `Copied ${copyLabel}` : "Copied")}
              className="absolute right-1.5 top-1.5 btn text-[11px] py-0.5 px-2"
            >Copy</button>
          </div>
        )}
      </div>
    </div>
  );
}

